import { type FC, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { MAX_PAGE_OUTCOMES } from "components/constants";
import { AddOutcomePlusButton } from "components/materials/page/outcomes/AddOutcomePlusButton";
import { PageOutcomeRubricPanel } from "components/materials/page/outcomes/PageOutcomeRubricPanel";
import { FeatureButton } from "components/materials/presentation/featuring/FeatureButton";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { CloseOverlayButton } from "components/materials/sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { AssessmentType } from "components/server-types";
import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import {
  NestedPageOutcomeType,
  pageSelectors,
  selectAssessmentsByPageId,
  selectIsEditModeEnabled,
  selectNestedPageOutcomes,
} from "store/selectors";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { pageIsDisabled } from "utils/page-utils";
import { FeatureStatus, usePresentation } from "utils/presentation";

const getAddButtonTooltip = (
  assessments: AssessmentType[],
  pageOutcomes: NestedPageOutcomeType[],
) => {
  if (assessments.length > 0) {
    return t("tooltip.outcome_config_has_assessments");
  }

  if (pageOutcomes.length >= MAX_PAGE_OUTCOMES) {
    return t("tooltip.outcome_config_limit_reached", { limit: MAX_PAGE_OUTCOMES });
  }

  return t("common.add.outcome");
};

interface PageOutcomeRubricSidebarProps {
  pageId: string;
}

export const PageOutcomeRubricSidebar: FC<PageOutcomeRubricSidebarProps> = ({ pageId }) => {
  const isAssessing = useIsAssessing();
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, pageId));
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, pageId));
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const addButtonDisabled =
    assessments.length > 0 || pageOutcomes.length >= MAX_PAGE_OUTCOMES || pageIsDisabled(page);

  const { isPresenting, featureStatus } = usePresentation();

  const canFeature = isPresenting && featureStatus === FeatureStatus.NONE && !!pageOutcomes.length;

  const [showShine, setShowShine] = useState(false);

  useEffect(() => {
    return addBroadcastListener(BROADCAST_TOPICS.AI_RUBRIC_GENERATED, (message) => {
      if (message.pageId === pageId) {
        setShowShine(true);
        // Reset after animation completes
        setTimeout(() => setShowShine(false), 1500);
      }
    });
  }, [pageId]);

  if (isAssessing) return null;

  return (
    <OverlayContainer
      className={twMerge("animate-shine-base bg-white", showShine && "animate-shine-active")}
    >
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex flex-grow items-center justify-between gap-2">
          <h3>{t("glossary.outcomes")}</h3>
          <div className="flex items-center gap-2">
            {isEditModeEnabled && (
              <AddOutcomePlusButton
                isDisabled={addButtonDisabled}
                pageId={pageId}
                title={getAddButtonTooltip(assessments, pageOutcomes)}
              />
            )}
            {canFeature && <FeatureButton pageId={pageId} size="s" isPresentingOutcomes />}
          </div>
        </div>

        <CloseOverlayButton />
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <PageOutcomeRubricPanel pageId={pageId} />
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
